import { APIError, createAuthMiddleware } from 'better-auth/api';
import { eq } from 'drizzle-orm';
import type { Database } from '@sunfish/db';
import { users } from '@sunfish/db/auth-schema';

export const IMPERSONATE_USER_PATH = '/admin/impersonate-user';
export const STOP_IMPERSONATING_PATH = '/admin/stop-impersonating';

type AuthMiddlewareContext = Parameters<Parameters<typeof createAuthMiddleware>[0]>[0];

// better-auth 1.4.5 has no target-role guard: any permitted impersonator can
// impersonate anyone, including other internal users. Reject internal targets
// before the endpoint runs (SUN-820).
export async function assertImpersonationTargetAllowed(db: Database, targetUserId: unknown): Promise<void> {
	if (typeof targetUserId !== 'string' || !targetUserId) {
		throw new APIError('BAD_REQUEST', { message: 'A target userId is required' });
	}

	const [target] = await db.select({ role: users.role }).from(users).where(eq(users.id, targetUserId)).limit(1);

	if (target?.role === 'internal') {
		throw new APIError('FORBIDDEN', { message: 'Internal users cannot be impersonated' });
	}
}

// impersonate-user creates the target session with dontRememberMe, and
// stop-impersonating restores the original session without unsetting that
// cookie. While it is set getSession stops writing the session cookie cache, so
// SSR reads keep serving the snapshot captured before impersonation: switching
// orgs looks like a no-op until you sign out. Restore the pre-impersonation
// cookie state, which the stashed admin_session value records (SUN-820).
async function restoreCookiesAfterImpersonation(ctx: AuthMiddlewareContext): Promise<void> {
	const adminCookie = ctx.context.createAuthCookie('admin_session');
	const stashed = await ctx.getSignedCookie(adminCookie.name, ctx.context.secret);
	const [, dontRememberMe] = (stashed || '').split(':');

	if (!dontRememberMe) {
		ctx.setCookie(ctx.context.authCookies.dontRememberToken.name, '', {
			...ctx.context.authCookies.dontRememberToken.options,
			maxAge: 0
		});
	}

	ctx.setCookie(adminCookie.name, '', { ...ctx.context.authCookies.sessionToken.options, maxAge: 0 });
}

export const impersonationCookieCleanup = createAuthMiddleware(async (ctx) => {
	// After hooks also run when the endpoint threw, and a failed stop leaves the
	// impersonated session in place.
	if (ctx.path === STOP_IMPERSONATING_PATH && !(ctx.context.returned instanceof APIError)) {
		await restoreCookiesAfterImpersonation(ctx);
	}
});
